import { allTopics } from './content-model.js'
import type {
  BandStatus,
  ContentDashboard,
  ContentLevel,
  TargetVocabulary,
  TopicReference,
} from './types.js'

export type VocabularyView =
  | 'all'
  | 'above'
  | 'below'
  | 'unregistered'
  | 'registered'
  | 'ungraded'
  | 'invalid'
  | 'repeated'
export type ExceptionState = 'none' | 'registered' | 'unregistered' | 'invalid'
export type ReuseState = 'single-use' | 'within-level' | 'cross-level'

export interface VocabularyOccurrence extends TargetVocabulary {
  level: string
  programId: string
  programLevelId: string
  programLevelContentKey: string
  themeId: string
  themePosition: number
  themeTitle: string
  topic: TopicReference
  exceptionState: ExceptionState
  invalidEntryKey?: string
}

export interface VocabularyRow {
  key?: string
  lemma: string
  occurrences: VocabularyOccurrence[]
  allOccurrences: VocabularyOccurrence[]
  reuse: ReuseState
}

export interface VocabularyFilters {
  view: VocabularyView
  q: string
  grade: string
  band: BandStatus | ''
  exception: ExceptionState | ''
  theme: string
  reuse: ReuseState | ''
}

export interface VocabularySummary {
  targetSlots: number
  uniqueLemmas: number
  inBandSlots: number
  belowBandSlots: number
  aboveBandSlots: number
  ungradedSlots: number
  invalidSlots: number
  unregisteredSlots: number
  repeatedLemmas: number
}

export const VOCABULARY_VIEWS: ReadonlyArray<{
  value: VocabularyView
  label: string
}> = [
  { value: 'all', label: 'All' },
  { value: 'above', label: 'Above band' },
  { value: 'below', label: 'Below band' },
  { value: 'unregistered', label: 'Missing approval' },
  { value: 'registered', label: 'Approved exceptions' },
  { value: 'ungraded', label: 'Ungraded' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'repeated', label: 'Repeated' },
]

const BAND_VALUES: BandStatus[] = ['in_window', 'above', 'below', 'ungraded', 'invalid']
const EXCEPTION_VALUES: ExceptionState[] = ['none', 'registered', 'unregistered', 'invalid']
const REUSE_VALUES: ReuseState[] = [
  'single-use',
  'within-level',
  'cross-level',
]
const VIEW_VALUES = VOCABULARY_VIEWS.map(({ value }) => value)

function exceptionState(target: TargetVocabulary): ExceptionState {
  // Ungraded counts as an exception too: the entry’s embedded approval is checked for
  // every out-of-window status, and an unwaived ungraded target blocks just
  // like an unwaived above-band one.
  if (target.bandStatus === 'invalid') return 'invalid'
  if (target.bandStatus === 'in_window') return 'none'
  return target.waived ? 'registered' : 'unregistered'
}

function occurrences(dashboard: ContentDashboard): VocabularyOccurrence[] {
  return allTopics(dashboard).flatMap(({ level, theme, topic }) =>
    topic.targetVocab.map((target, index) => ({
      ...target,
      invalidEntryKey: target.bandStatus === 'invalid' ? JSON.stringify([topic.id, index]) : undefined,
      level: level.level,
      programLevelContentKey: level.programLevelContentKey,
      programId: level.programId,
      programLevelId: level.programLevelId,
      themeId: theme.id,
      themePosition: theme.position,
      themeTitle: theme.title.en,
      topic: {
        id: topic.id,
        level: level.level,
        programLevelContentKey: level.programLevelContentKey,
        programId: level.programId,
        programLevelId: level.programLevelId,
        position: topic.position,
        title: topic.title.en,
      },
      exceptionState: exceptionState(target),
    })),
  )
}

function groupByLemma(
  rows: VocabularyOccurrence[],
): Map<string, VocabularyOccurrence[]> {
  const groups = new Map<string, VocabularyOccurrence[]>()
  for (const row of rows) {
    const key = row.invalidEntryKey === undefined
      ? JSON.stringify(["lemma", row.canonicalLemma])
      : JSON.stringify(["invalid", row.invalidEntryKey])
    const group = groups.get(key) ?? []
    group.push(row)
    groups.set(key, group)
  }
  return groups
}

export function vocabularyRows(
  dashboard: ContentDashboard,
  level: ContentLevel,
): VocabularyRow[] {
  const everyOccurrence = occurrences(dashboard)
  const byLemma = groupByLemma(everyOccurrence)
  const selected = everyOccurrence.filter((row) => row.programLevelId === level.programLevelId)
  return Array.from(groupByLemma(selected)).map(
    ([lemma, rows]): VocabularyRow => {
      const allRows = byLemma.get(lemma) ?? rows
      const programCount = new Set(allRows.map((row) => row.programLevelId)).size
      return {
        key: lemma,
        lemma: rows[0]?.bandStatus === 'invalid' ? rows[0].en || 'Missing word' : rows[0]!.canonicalLemma,
        occurrences: rows,
        allOccurrences: allRows,
        reuse:
          programCount > 1
            ? 'cross-level'
            : rows.length > 1
              ? 'within-level'
              : 'single-use',
      }
    },
  ).sort((left, right) => left.lemma.localeCompare(right.lemma))
}

export function vocabularySummary(
  rows: VocabularyRow[],
): VocabularySummary {
  const allSlots = rows.flatMap((row) => row.occurrences)
  const slots = allSlots.filter((row) => row.bandStatus !== 'invalid')
  return {
    targetSlots: slots.length,
    uniqueLemmas: rows.filter((row) => row.occurrences.some((item) => item.bandStatus !== 'invalid')).length,
    invalidSlots: allSlots.length - slots.length,
    inBandSlots: slots.filter((row) => row.bandStatus === 'in_window').length,
    belowBandSlots: slots.filter((row) => row.bandStatus === 'below').length,
    aboveBandSlots: slots.filter((row) => row.bandStatus === 'above').length,
    ungradedSlots: slots.filter((row) => row.bandStatus === 'ungraded').length,
    unregisteredSlots: slots.filter(
      (row) => row.exceptionState === 'unregistered',
    ).length,
    repeatedLemmas: rows.filter((row) => row.reuse !== 'single-use').length,
  }
}

export function readVocabularyFilters(
  searchParams: URLSearchParams,
): VocabularyFilters {
  const value = <T extends string>(key: string, values: T[]): T | '' => {
    const candidate = searchParams.get(key) as T | null
    return candidate && values.includes(candidate) ? candidate : ''
  }
  return {
    view: value('view', VIEW_VALUES) || 'all',
    q: searchParams.get('q') ?? '',
    grade: searchParams.get('grade')?.toUpperCase() ?? '',
    band: value('band', BAND_VALUES),
    exception: value('exception', EXCEPTION_VALUES),
    theme: searchParams.get('theme') ?? '',
    reuse: value('reuse', REUSE_VALUES),
  }
}

export function vocabularySearchParams(
  level: string,
  filters: VocabularyFilters,
): URLSearchParams {
  const params = new URLSearchParams({ level })
  if (filters.view !== 'all') params.set('view', filters.view)
  for (const key of ['q', 'grade', 'band', 'exception', 'theme', 'reuse'] as const) {
    if (filters[key]) params.set(key, filters[key])
  }
  return params
}

export function filterVocabularyRows(
  rows: VocabularyRow[],
  filters: VocabularyFilters,
): VocabularyRow[] {
  const viewMatches = (item: VocabularyOccurrence): boolean => {
    if (filters.view === 'above') return item.bandStatus === 'above'
    if (filters.view === 'below') return item.bandStatus === 'below'
    if (filters.view === 'unregistered') {
      return item.exceptionState === 'unregistered'
    }
    if (filters.view === 'registered') {
      return item.exceptionState === 'registered'
    }
    if (filters.view === 'invalid') return item.bandStatus === 'invalid'
    if (filters.view === 'ungraded') return item.bandStatus === 'ungraded'
    return true
  }
  const query = filters.q.trim().toLocaleLowerCase()
  return rows.filter((row) => {
    if (filters.view === 'repeated' && row.reuse === 'single-use') return false
    if (filters.reuse && row.reuse !== filters.reuse) return false
    const lemmaMatchesQuery = row.lemma.toLocaleLowerCase().includes(query)
    return row.occurrences.some(
      (item) =>
        viewMatches(item) &&
        (!filters.grade || item.cefrLevel === filters.grade) &&
        (!filters.band || item.bandStatus === filters.band) &&
        (!filters.exception || item.exceptionState === filters.exception) &&
        (!filters.theme || item.themeId === filters.theme) &&
        (!query ||
          lemmaMatchesQuery ||
          [item.en, item.ja, item.themeTitle, item.topic.title].some((value) =>
            value.toLocaleLowerCase().includes(query),
          )),
    )
  })
}
